import { DataGrid, type GridColumn } from "@/components/data-grid";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useMemo, useState } from "react";

import { errorMessage } from "../../lib/query.ts";

import { useSession } from "../../app/session.tsx";
import {
  formatCell,
  useDataset,
  type DatasetColumn,
  type DatasetRow,
} from "../dataset.ts";
import { CHAT_TEXTS as TEXTS } from "../texts.ts";
import { Figure } from "./Figure.tsx";

/** Filas visibles antes de pedir "ver más": una tabla larga no se lee, se busca. */
const PAGE = 25;

interface Props {
  props: Record<string, unknown>;
}

/**
 * Tabla, de dos procedencias.
 *
 * Con `datasetId` las filas se piden aparte: es lo que evita que el modelo
 * transcriba números (sección 9) y lo que hace viable una tabla de miles de
 * filas.
 *
 * Con `rows` las filas vienen ya en el resultado de la herramienta. Tampoco
 * las transcribe el modelo -- las produjo la consulta -- y para un listado
 * corto montar un dataset sería dar un rodeo para volver al mismo sitio.
 */
export function DataTable({ props }: Props) {
  const datasetId =
    typeof props["datasetId"] === "string" ? props["datasetId"] : null;
  const rows = Array.isArray(props["rows"])
    ? (props["rows"] as DatasetRow[])
    : null;
  const [visible, setVisible] = useState(PAGE);
  /**
   * Sin marco: lo pide el gráfico cuando enseña su tabla, porque el marco ya
   * lo pone él. Dos marcos anidados se ven como un error y lo son.
   */
  const bare = props["bare"] === true;
  const title = typeof props["title"] === "string" ? props["title"] : null;

  if (datasetId !== null) {
    return (
      <Loaded
        datasetId={datasetId}
        title={title}
        visible={visible}
        onMore={setVisible}
        bare={bare}
      />
    );
  }

  if (rows !== null) {
    return (
      <Table
        title={title}
        columns={columnsOf(props, rows)}
        rows={rows}
        visible={visible}
        onMore={setVisible}
        bare={bare}
      />
    );
  }

  return (
    <Alert variant="destructive">
      <AlertTitle>{TEXTS.table.noDataTitle}</AlertTitle>
      <AlertDescription>{TEXTS.table.noData}</AlertDescription>
    </Alert>
  );
}

/**
 * Las columnas declaradas, o deducidas de las filas.
 *
 * Deducirlas recorre TODAS las filas y no solo la primera: una fila puede
 * traer un campo nulo que otra sí tiene, y quedarse con la primera perdería
 * esa columna.
 */
const columnsOf = (
  props: Record<string, unknown>,
  rows: readonly DatasetRow[],
): DatasetColumn[] => {
  if (Array.isArray(props["columns"])) {
    return props["columns"] as DatasetColumn[];
  }

  const keys = [...new Set(rows.flatMap((row) => Object.keys(row)))];
  return keys.map((key) => ({
    key,
    label: key,
    type: rows.some((row) => typeof row[key] === "number") ? "number" : "text",
  }));
};

/** Carga el dataset y delega en la misma tabla que usan las filas en linea. */
function Loaded({
  datasetId,
  title,
  visible,
  onMore,
  bare,
}: {
  datasetId: string;
  /**
   * El título que puso la herramienta, que manda sobre el del dataset.
   *
   * Una vista previa guarda la tabla entera como dataset (§9) y lo que la
   * resume --"312 de 480 filas escribirían algo"-- lo calcula ella al
   * dibujar. El nombre del dataset es solo de dónde salió.
   */
  title: string | null;
  visible: number;
  onMore: (n: number) => void;
  bare: boolean;
}) {
  const userId = useSession().me?.userId ?? "";
  const state = useDataset(datasetId, userId);

  if (state.isPending) {
    return (
      <div className="rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground">
        {TEXTS.table.loading}
      </div>
    );
  }

  if (state.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{TEXTS.table.loadError}</AlertTitle>
        <AlertDescription>{errorMessage(state.error)}</AlertDescription>
      </Alert>
    );
  }

  const dataset = state.data;
  return (
    <Table
      title={title ?? dataset.name}
      columns={dataset.columns}
      rows={dataset.rows}
      // Cuantas hay DE VERDAD. Una tabla de calculo puede traer 100.000 filas
      // y el backend manda solo las que se pueden dibujar; decir el tamano de
      // lo que llego seria mentir sobre el tamano del resultado.
      total={dataset.rowCount}
      visible={visible}
      onMore={onMore}
      bare={bare}
    />
  );
}

function Table({
  title,
  columns,
  rows,
  total: declared,
  visible,
  onMore,
  bare,
}: {
  title: string | null;
  columns: readonly DatasetColumn[];
  rows: readonly DatasetRow[];
  /** Cuantas hay en el resultado. Sin esto, las que llegaron. */
  total?: number;
  visible: number;
  onMore: (n: number) => void;
  bare: boolean;
}) {
  /**
   * CUANTAS LLEGARON y CUANTAS HAY no son lo mismo.
   *
   * Una tabla de calculo puede tener 100.000 filas y el backend manda las que
   * se pueden dibujar. "Ver mas" solo puede ofrecer lo que llego; el rotulo
   * tiene que decir lo que hay, o el usuario cree que su lote son 5.000
   * personas cuando son 6.000.
   */
  const loaded = rows.length;
  const total = declared ?? loaded;

  /**
   * Las columnas se memorizan: la rejilla reconstruye la tabla entera si
   * cambian de identidad, y sin esto cambiarían en cada render.
   */
  const gridColumns = useMemo(
    () =>
      columns.map((column): GridColumn<DatasetRow> => ({
        key: column.key,
        label: column.label,
        numeric: column.type === "number",
        render: (row) => formatCell(row[column.key] ?? null, column.type),
        sortValue: (row) => row[column.key] ?? null,
      })),
    [columns],
  );

  // "Ver más" en vez de paginar: una tabla de resultados se lee de arriba
  // abajo, y partirla en páginas obliga a recordar en cuál se estaba.
  const shown = useMemo(() => rows.slice(0, visible), [rows, visible]);

  const grid = (
    <div className="scroll-quiet max-h-104 overflow-auto">
      <DataGrid columns={gridColumns} rows={shown} />
    </div>
  );

  const more =
    visible < loaded ? (
      <button
        type="button"
        className="w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
        onClick={() => {
          onMore(visible + PAGE * 4);
        }}
      >
        {TEXTS.table.more((loaded - visible).toLocaleString("es"))}
      </button>
    ) : null;

  if (bare) {
    return (
      <>
        {grid}
        {more !== null && <div className="border-t">{more}</div>}
      </>
    );
  }

  return (
    <Figure
      title={title ?? TEXTS.table.result}
      aside={
        <span className="text-xs text-muted-foreground tabular-nums">
          {TEXTS.table.rows(total.toLocaleString("es"), total === 1)}
          {total > loaded && TEXTS.table.shown(loaded.toLocaleString("es"))}
        </span>
      }
      {...(more !== null ? { footer: more } : {})}
    >
      {grid}
    </Figure>
  );
}
